import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { AdminWallet } from '../../interfaces/admin-wallet.interface';
import { AdminWalletsService } from '../../services/admin-wallets.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-admin-wallets',
  templateUrl: './admin-wallets.component.html',
  styleUrls: ['./admin-wallets.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminWalletsComponent implements OnInit {
  wallets: AdminWallet[] = [];
  dataSource = new MatTableDataSource<AdminWallet>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  isModalOpen = false;
  isEditing = false;
  editingWalletId: string | null = null;
  walletForm!: FormGroup;
  loading = false;
  expandedCardId: number | null = null;

  // Opciones para los selects del formulario
  operators = ['BINANCE'];
  cryptos = ['USDT'];
  networks = ['ETH', 'TRX', 'BSC', 'SOL'];

  constructor(
    private fb: FormBuilder,
    private adminWalletsService: AdminWalletsService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) {
    this.initForm();
  }

  private initForm(): void {
    this.walletForm = this.fb.group({
      operator: [this.operators[0], Validators.required],
      crypto: [this.cryptos[0], Validators.required],
      network: [this.networks[0], Validators.required],
      address: ['', [Validators.required, Validators.minLength(10)]],
      isActive: [true],
    });
  }

  ngOnInit(): void {
    this.loadWallets();
    this.adminWalletsService.loading$.subscribe((isLoading) => {
      this.loading = isLoading;
    });
  }

  loadWallets(): void {
    this.adminWalletsService.findAll().subscribe({
      next: (data) => {
        this.wallets = data;
        this.dataSource.data = data;
        this.cdr.markForCheck();
      },
      error: (err) => {
        let msg =
          err?.message ||
          err?.error?.message ||
          err?.toString() ||
          'Error al cargar las wallets';
        if (err?.code_error) {
          msg += ` (${err.code_error})`;
        }
        this.showErrorMessage(msg);
        this.cdr.markForCheck();
      },
    });
  }

  ngAfterViewInit(): void {
    // Asigna el paginador al datasource después de que la vista está inicializada
    this.dataSource.paginator = this.paginator;
    this.cdr.markForCheck();
  }

  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  openModal(): void {
    this.isModalOpen = true;
    this.initForm();
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.isEditing = false;
    this.editingWalletId = null;
    this.walletForm.reset({
      operator: this.operators[0],
      crypto: this.cryptos[0],
      network: this.networks[0],
      isActive: true,
    });
  }

  openEditModal(wallet: AdminWallet): void {
    this.isModalOpen = true;
    this.isEditing = true;
    this.editingWalletId = wallet.id;
    this.walletForm.patchValue({
      operator: wallet.operator,
      crypto: wallet.crypto,
      network: wallet.network,
      address: wallet.address,
      isActive: wallet.isActive,
    });
  }

  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const sanitizedText = pastedText.replace(/\s+/g, '');
    this.walletForm.get('address')?.setValue(sanitizedText);
  }

  onSubmit(): void {
    if (this.walletForm.invalid) {
      this.walletForm.markAllAsTouched();
      return;
    }

    const formValue = this.walletForm.value;

    if (this.isEditing && this.editingWalletId) {
      this.adminWalletsService
        .update(this.editingWalletId, formValue)
        .subscribe({
          next: () => {
            this.closeModal();
            this.showSuccessMessage('Wallet actualizada exitosamente');
            this.loadWallets();
          },
          error: (err) => {
            let msg =
              err?.message ||
              err?.error?.message ||
              err?.toString() ||
              'Error al actualizar la wallet';
            if (err?.code_error) {
              msg += ` (${err.code_error})`;
            }
            this.closeModal();
            this.showErrorMessage(msg);
          },
        });
    } else {
      this.adminWalletsService.create(formValue).subscribe({
        next: () => {
          this.closeModal();
          this.showSuccessMessage('Wallet creada exitosamente');
          this.loadWallets();
        },
        error: (err) => {
          let msg =
            err?.message ||
            err?.error?.message ||
            err?.toString() ||
            'Error al crear la wallet';
          if (err?.code_error) {
            msg += ` (${err.code_error})`;
          }
          this.closeModal();
          this.showErrorMessage(msg);
        },
      });
    }
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['success-snackbar'],
    });
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['error-snackbar'],
    });
  }

  toggleWalletStatus(wallet: AdminWallet): void {
    const updatedStatus = !wallet.isActive;
    this.adminWalletsService
      .update(wallet.id, { isActive: updatedStatus })
      .subscribe({
        next: () => {
          this.showSuccessMessage(
            `Wallet ${updatedStatus ? 'activada' : 'desactivada'} exitosamente`,
          );
          this.loadWallets();
        },
        error: (err) => {
          let msg =
            err?.message ||
            err?.error?.message ||
            err?.toString() ||
            'Error al actualizar el estado de la wallet';
          if (err?.code_error) {
            msg += ` (${err.code_error})`;
          }
          this.showErrorMessage(msg);
        },
      });
  }

  async confirmAndDeleteWallet(wallet: AdminWallet): Promise<void> {
    const result = await Swal.fire({
      title: '¿Eliminar wallet?',
      html: `¿Está seguro que desea eliminar la wallet de <b>${wallet.operator}</b> (<b>${wallet.crypto} - ${wallet.network}</b>)?<br><span class='text-red-600 font-bold'>Esta acción no se puede deshacer.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });
    if (result.isConfirmed) {
      this.adminWalletsService.deleteWallet(wallet.id).subscribe({
        next: () => {
          Swal.fire(
            'Eliminada',
            'La wallet fue eliminada exitosamente.',
            'success',
          );
          this.loadWallets();
        },
        error: (err: any) => {
          let msg =
            err?.message ||
            err?.error?.message ||
            err?.toString() ||
            'Error al eliminar la wallet';
          if (err?.code_error) {
            msg += ` (${err.code_error})`;
          }
          Swal.fire('Error', msg, 'error');
        },
      });
    }
  }
}
